import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"
import { French } from "flatpickr/dist/l10n/fr.js"

export default class extends Controller {
  static targets = ["input", "periodLabel", "applyButton"]

  connect() {
    this.selectedDates = []
    this.picker = null
    this.element.dataset.dateFrom = ""
    this.element.dataset.dateTo = ""
    this.boundAdjustPosition = this.adjustPosition.bind(this)
    window.addEventListener("resize", this.boundAdjustPosition)
  }

  disconnect() {
    if (this.picker) {
      this.picker.destroy()
      this.picker = null
    }
    window.removeEventListener("resize", this.boundAdjustPosition)
  }

  open() {
    if (!this.picker) {
      this.initFlatpickr()
    }
    requestAnimationFrame(() => this.adjustPosition())
  }

  adjustPosition() {
    if (!this.element.classList.contains("open")) return

    if (window.innerWidth < 768) {
      this.element.style.transform = ""
      return
    }

    this.element.style.transform = ""
    const rect = this.element.getBoundingClientRect()
    const overflow = rect.right - window.innerWidth + 8

    if (overflow > 0) {
      this.element.style.transform = `translateX(-${overflow}px)`
    }
  }

  clear() {
    if (this.picker) this.picker.clear()
    this.selectedDates = []
    this.element.dataset.dateFrom = ""
    this.element.dataset.dateTo = ""
    this.updatePeriodLabel()
    this.updateApplyButton()
  }

  // private

  initFlatpickr() {
    const locale = document.querySelector("html").getAttribute("lang") === "fr" ? French : undefined
    const isMobile = window.innerWidth < 768

    const config = {
      mode: "range",
      showMonths: isMobile ? 1 : 2,
      dateFormat: "Y-m-d",
      locale: locale,
      inline: true,
      onChange: this.handleDateChange.bind(this)
    }

    const fromDate = this.element.dataset.dateFrom
    const toDate = this.element.dataset.dateTo
    if (fromDate && toDate) {
      config.defaultDate = [fromDate, toDate]
    }

    this.picker = flatpickr(this.inputTarget, config)

    if (fromDate && toDate) {
      this.selectedDates = this.picker.selectedDates
      this.updatePeriodLabel()
      this.updateApplyButton()
    }
  }

  handleDateChange(selectedDates) {
    this.selectedDates = selectedDates
    if (selectedDates.length === 2) {
      this.element.dataset.dateFrom = this.formatDate(selectedDates[0])
      this.element.dataset.dateTo = this.formatDate(selectedDates[1])
    }
    this.updatePeriodLabel()
    this.updateApplyButton()
  }

  updatePeriodLabel() {
    if (!this.hasPeriodLabelTarget) return

    if (this.selectedDates.length === 2) {
      const from = this.formatDisplayDate(this.selectedDates[0])
      const to = this.formatDisplayDate(this.selectedDates[1])
      this.periodLabelTarget.textContent = `${from} – ${to}`
      this.periodLabelTarget.classList.remove("d-none")
    } else {
      this.periodLabelTarget.textContent = ""
      this.periodLabelTarget.classList.add("d-none")
    }
  }

  updateApplyButton() {
    if (!this.hasApplyButtonTarget) return

    if (this.selectedDates.length === 2) {
      this.applyButtonTarget.removeAttribute("disabled")
    } else {
      this.applyButtonTarget.setAttribute("disabled", "disabled")
    }
  }

  formatDate(date) {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
  }

  formatDisplayDate(date) {
    return date.toLocaleDateString(document.querySelector("html").getAttribute("lang") || "fr", {
      day: "numeric",
      month: "long",
      year: "numeric"
    })
  }
}
